import React, { useState, useMemo } from 'react';
import { documentConfig } from '../../constants/documentConfig';
import { NORMALIZED_TITLE_KEYWORDS } from '../../constants/resumeKeywords';
import { cleanNumbersForDownload } from '../../utils/downloadUtils';

/* ── Inline text: convert **bold** to <strong> ── */
function renderInline(text, key) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <React.Fragment key={key}>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </React.Fragment>
  );
}

/* ── Helpers ── */
function isBullet(line) {
  return /^\s*[-•*]\s+/.test(line);
}

function isCoverLetterContactField(line) {
  const t = line.trim();
  if (/\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/.test(t)) return true;
  if (/@/.test(t)) return true;
  if (/[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s+[A-Z]{2}\b/.test(t)) return true;
  if (/linkedin\.com/i.test(t)) return true;
  return false;
}

/* ── Resume / CV renderer — 9-rule state machine, mirrors resumeRenderer.js ── */
function renderResumeLike(lines) {
  const els = [];
  let i = 0;
  let currentSection = '';

  // Header pre-pass: skip leading blanks, then emit name + contact lines
  while (i < lines.length && !lines[i].trim()) i++;

  if (i < lines.length) {
    // First non-empty line = candidate name
    els.push(
      <p key={`name${i}`} className="doc-candidate-name">
        {lines[i].trim()}
      </p>
    );
    i++;

    // Subsequent non-empty lines before first blank or section header
    while (i < lines.length) {
      const ct = lines[i].trim();
      if (!ct) break;
      // Stop if this line would be a section header (RULE 2/3 conditions)
      const ctHasDash = /\s[-–]\s/.test(ct);
      const ctHasPunct = /[.!?,;]$/.test(ct);
      if (
        (!ctHasDash && !ctHasPunct && ct.length <= 60 && /[A-Z]/.test(ct) && ct === ct.toUpperCase()) ||
        (!ctHasDash && NORMALIZED_TITLE_KEYWORDS.has(ct.toLowerCase()))
      ) break;
      // LinkedIn line in header → hyperlink, centered
      if (/linkedin\.com/i.test(ct)) {
        const urlMatch = ct.match(/(https?:\/\/)?(?:www\.)?linkedin\.com\/[^\s]*/i);
        if (urlMatch) {
          const rawUrl = urlMatch[0];
          const href = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
          const before = ct.slice(0, urlMatch.index);
          const after = ct.slice(urlMatch.index + rawUrl.length);
          els.push(
            <p key={`li${i}`} className="doc-contact-line">
              {before}<a href={href} target="_blank" rel="noopener noreferrer">{rawUrl}</a>{after}
            </p>
          );
          i++;
          continue;
        }
      }
      // Contact line: contains @, digit, or comma
      if (/@|\d|,/.test(ct)) {
        els.push(<p key={`cl${i}`} className="doc-contact-line">{renderInline(ct, 0)}</p>);
        i++;
        continue;
      }
      break; // doesn't match contact pattern — let main loop handle it
    }
  }

  while (i < lines.length) {
    const raw = lines[i];
    const t = raw.trim();

    // RULE 0 — empty line
    if (!t) {
      els.push(<div key={`sp${i}`} className="doc-spacer" />);
      i++;
      continue;
    }

    // RULE 1 — LinkedIn line → hyperlink
    if (/linkedin\.com/i.test(t)) {
      const urlMatch = t.match(/(https?:\/\/)?(?:www\.)?linkedin\.com\/[^\s]*/i);
      if (urlMatch) {
        const rawUrl = urlMatch[0];
        const href = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
        const before = t.slice(0, urlMatch.index);
        const after = t.slice(urlMatch.index + rawUrl.length);
        els.push(
          <p key={`li${i}`} className="doc-paragraph">
            {before}<a href={href} target="_blank" rel="noopener noreferrer">{rawUrl}</a>{after}
          </p>
        );
      } else {
        els.push(<p key={`p${i}`} className="doc-paragraph">{renderInline(t, 0)}</p>);
      }
      i++;
      continue;
    }

    const hasDash = /\s[-–]\s/.test(t);
    const hasSentencePunct = /[.!?,;]$/.test(t);
    const hasDate = /(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Present|\d{4})/i.test(t);

    // RULE 2 — ALL-CAPS, no dash, ≤60 chars, has letter, no sentence punctuation
    if (!hasDash && !hasSentencePunct && t.length <= 60 && /[A-Z]/.test(t) && t === t.toUpperCase()) {
      currentSection = t.toLowerCase();
      els.push(<h2 key={`h${i}`} className="doc-section-header">{t}</h2>);
      i++;
      continue;
    }

    // RULE 3 — NORMALIZED_TITLE_KEYWORDS match, no dash
    if (!hasDash && NORMALIZED_TITLE_KEYWORDS.has(t.toLowerCase())) {
      currentSection = t.toLowerCase();
      els.push(<h2 key={`h${i}`} className="doc-section-header">{t}</h2>);
      i++;
      continue;
    }

    // RULE 4 — education / technical / additional → always plain paragraph, no bold/italic
    if (
      currentSection.includes('education') ||
      currentSection.includes('technical') ||
      currentSection.includes('additional')
    ) {
      els.push(<p key={`p${i}`} className="doc-paragraph">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    // RULE 5 — job title line (dash + date + experience section)
    if (hasDash && hasDate && currentSection.includes('experience')) {
      els.push(<p key={`jt${i}`} className="doc-jobtitle-line">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    // RULE 6 — company line (dash + City, State + no date + experience section)
    const hasCityState = /[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s+[A-Z]{2}\b/.test(t);
    if (hasDash && hasCityState && !hasDate && currentSection.includes('experience')) {
      els.push(<p key={`co${i}`} className="doc-company-line">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    // RULE 7 — scope line (Oversaw/Led/Directed/Managed/Owned, no date)
    if (/^(?:Oversaw|Led|Directed|Managed|Owned)\b/i.test(t) && !hasDate) {
      els.push(<p key={`sc${i}`} className="doc-scope-line">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    // RULE 8 — bullet •
    if (/^\s*•/.test(raw)) {
      const isSkillsSection =
        currentSection.includes('competencies') || currentSection.includes('skills');

      if (isSkillsSection) {
        const bullets = [];
        while (i < lines.length && /^\s*•/.test(lines[i])) {
          bullets.push(lines[i].trim().replace(/^•\s*/, ''));
          i++;
        }
        els.push(
          <p key={`sk${i}`} className="doc-paragraph">
            {renderInline(bullets.join(' • '), 0)}
          </p>
        );
      } else {
        const bullets = [];
        while (i < lines.length && /^\s*•/.test(lines[i])) {
          bullets.push(lines[i].trim().replace(/^•\s*/, ''));
          i++;
        }
        els.push(
          <ul key={`ul${i}`} className="doc-bullet-list">
            {bullets.map((b, bi) => (
              <li key={bi} className="doc-bullet-item">{renderInline(b, bi)}</li>
            ))}
          </ul>
        );
      }
      continue;
    }

    // RULE 9 — everything else
    els.push(<p key={`p${i}`} className="doc-paragraph">{renderInline(t, 0)}</p>);
    i++;
  }

  return els;
}

/* ── Cover letter renderer ── */
function renderCoverLetter(lines) {
  const els = [];
  let phase = 'header'; // header → body → closing
  // Within header: name collects the sender name, contact collects
  // phone/email/city into one consolidated line, LinkedIn gets its own
  // anchor line, recipient renders the hiring manager block.
  let headerState = 'name';
  const contactParts = [];
  let clLinkedInUrl = null;
  let i = 0;

  const flushContact = (key) => {
    if (contactParts.length === 0 && !clLinkedInUrl) return;
    if (contactParts.length > 0) {
      els.push(
        <p key={`contact${key}`} className="doc-contact-line">
          {contactParts.join(' • ')}
        </p>
      );
      contactParts.length = 0;
    }
    if (clLinkedInUrl) {
      const displayUrl = clLinkedInUrl.replace(/^https?:\/\//, '');
      els.push(
        <p key={`linkedin${key}`} className="doc-contact-line">
          <a href={clLinkedInUrl} target="_blank" rel="noopener noreferrer">{displayUrl}</a>
        </p>
      );
      clLinkedInUrl = null;
    }
  };

  while (i < lines.length) {
    const raw = lines[i];
    const t = raw.trim();

    if (!t) {
      if (phase === 'header' && headerState === 'contact') {
        flushContact(i);
        headerState = 'recipient';
      } else if (phase !== 'header' || headerState !== 'name') {
        els.push(<div key={`sp${i}`} className="doc-spacer" />);
      }
      i++;
      continue;
    }

    // Detect greeting → switch to body
    if (phase === 'header' && /^Dear\s/i.test(t)) {
      flushContact(i);
      phase = 'body';
      els.push(<p key={`greet${i}`} className="doc-greeting">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    // Subject / Re: line in header
    if (phase === 'header' && /^(Re:|Subject:|RE:|SUBJECT:)/i.test(t)) {
      flushContact(i);
      els.push(<p key={`subj${i}`} className="doc-subject-line">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    // Detect closing
    if (
      phase === 'body' &&
      /^(Sincerely|Best regards|Kind regards|Warm regards|Respectfully|Thank you|Yours truly|Regards)/i.test(t)
    ) {
      phase = 'closing';
      els.push(<p key={`close${i}`} className="doc-closing">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    if (phase === 'header') {
      if (headerState === 'name') {
        els.push(<p key={`clname${i}`} className="doc-candidate-name">{renderInline(t, 0)}</p>);
        headerState = 'contact';
      } else if (headerState === 'contact') {
        if (isCoverLetterContactField(t)) {
          if (/linkedin\.com/i.test(t)) {
            const urlMatch = t.match(/(https?:\/\/)?(?:www\.)?linkedin\.com\/[^\s]*/i);
            if (urlMatch && !clLinkedInUrl) {
              const raw = urlMatch[0];
              clLinkedInUrl = raw.startsWith('http') ? raw : `https://${raw}`;
            }
            // Non-LinkedIn parts of the same line (e.g. "email • phone • linkedin")
            if (urlMatch) {
              const before = t.slice(0, urlMatch.index).replace(/\s*[•|]\s*$/, '').trim();
              const after = t.slice(urlMatch.index + urlMatch[0].length).replace(/^\s*[•|]\s*/, '').trim();
              if (before) contactParts.push(before);
              if (after) contactParts.push(after);
            }
          } else {
            contactParts.push(t);
          }
        } else {
          // Non-contact line ends the contact block → flush and move to recipient
          flushContact(i);
          headerState = 'recipient';
          els.push(<p key={`hdr${i}`} className="doc-header-line">{renderInline(t, 0)}</p>);
        }
      } else {
        // recipient block — hiring manager, company, address, date
        els.push(<p key={`hdr${i}`} className="doc-header-line">{renderInline(t, 0)}</p>);
      }
      i++;
      continue;
    }

    if (phase === 'body') {
      if (isBullet(t)) {
        const bullets = [];
        while (i < lines.length && isBullet(lines[i])) {
          bullets.push(lines[i].trim().replace(/^[-•*]\s+/, ''));
          i++;
        }
        els.push(
          <ul key={`ul${i}`} className="doc-bullet-list">
            {bullets.map((b, bi) => (
              <li key={bi} className="doc-bullet-item">{renderInline(b, bi)}</li>
            ))}
          </ul>
        );
        continue;
      }
      els.push(<p key={`body${i}`} className="doc-paragraph">{renderInline(t, 0)}</p>);
      i++;
      continue;
    }

    // closing
    els.push(<p key={`cl${i}`} className="doc-closing-line">{renderInline(t, 0)}</p>);
    i++;
  }

  return els;
}

/* ── LinkedIn / Bio / Portfolio: narrative renderer ─────────────────────
   Skips the resume header pre-pass (no name / contact block detection).
   Uses the same section-heading and bullet rules as renderResumeLike but
   treats every line as body content — no experience-section special cases. */
function renderNarrative(lines) {
  const els = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const t = raw.trim();

    // RULE 0 — empty line
    if (!t) {
      els.push(<div key={`sp${i}`} className="doc-spacer" />);
      i++;
      continue;
    }

    // RULE 1 — LinkedIn / URL hyperlink
    if (/linkedin\.com/i.test(t)) {
      const urlMatch = t.match(/(https?:\/\/)?(?:www\.)?linkedin\.com\/[^\s]*/i);
      if (urlMatch) {
        const rawUrl = urlMatch[0];
        const href = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
        const before = t.slice(0, urlMatch.index);
        const after = t.slice(urlMatch.index + rawUrl.length);
        els.push(
          <p key={`li${i}`} className="doc-paragraph">
            {before}
            <a href={href} target="_blank" rel="noopener noreferrer">{rawUrl}</a>
            {after}
          </p>
        );
      } else {
        els.push(<p key={`p${i}`} className="doc-paragraph">{renderInline(t, 0)}</p>);
      }
      i++;
      continue;
    }

    const hasDash = /\s[-–]\s/.test(t);
    const hasSentencePunct = /[.!?,;]$/.test(t);

    // RULE 2 — ALL-CAPS section header (no dash, ≤60 chars, no sentence punctuation)
    if (!hasDash && !hasSentencePunct && t.length <= 60 && /[A-Z]/.test(t) && t === t.toUpperCase()) {
      els.push(<h2 key={`h${i}`} className="doc-section-header">{t}</h2>);
      i++;
      continue;
    }

    // RULE 3 — Known Title-Case section keyword
    if (!hasDash && NORMALIZED_TITLE_KEYWORDS.has(t.toLowerCase())) {
      els.push(<h2 key={`h${i}`} className="doc-section-header">{t}</h2>);
      i++;
      continue;
    }

    // RULE 4 — Bullet list
    if (/^\s*[-•*]\s+/.test(raw) || /^\s*•/.test(raw)) {
      const bullets = [];
      while (i < lines.length && (/^\s*[-•*]\s+/.test(lines[i]) || /^\s*•/.test(lines[i]))) {
        bullets.push(lines[i].trim().replace(/^[-•*]\s+/, '').replace(/^•\s*/, ''));
        i++;
      }
      els.push(
        <ul key={`ul${i}`} className="doc-bullet-list">
          {bullets.map((b, bi) => (
            <li key={bi} className="doc-bullet-item">{renderInline(b, bi)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // RULE 5 — everything else: plain paragraph
    els.push(<p key={`p${i}`} className="doc-paragraph">{renderInline(t, 0)}</p>);
    i++;
  }

  return els;
}

/* ── Main dispatcher ── */
function renderDocumentContent(text, docType) {
  if (!text || !text.trim()) {
    return [<p key="empty" className="doc-empty">No content generated yet.</p>];
  }

  // Run the same cleaning pipeline used by the download path so the preview
  // and the DOCX are always in sync. cleanNumbersForDownload() strips bold
  // markers, frequency counts, Draft Mode annotations, and JSON bleed.
  const cleaned = cleanNumbersForDownload(text, docType);

  const lines = cleaned.split('\n');

  if (docType === 'coverLetter') return renderCoverLetter(lines);
  if (docType === 'resume' || docType === 'cv') return renderResumeLike(lines);
  return renderNarrative(lines); // linkedin, bio, portfolio
}

/* ── Print document as PDF ── */
function printDocument(result, label) {
  const el = document.querySelector('.doc-page');
  if (!el) return;
  const html = el.innerHTML;

  const win = window.open('', '_blank', 'width=800,height=1000');
  if (!win) return;
  win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${label}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono&display=swap');
    body { margin: 0; padding: 40px; font-family: Georgia, serif; font-size: 13px; line-height: 1.65; color: #1a1a1a; }
    h2 { font-size: 13px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #000; padding-bottom: 3px; margin: 18px 0 6px; }
    p { margin-bottom: 8px; }
    ul { margin: 4px 0 10px 18px; padding: 0; }
    li { margin-bottom: 4px; }
    a { color: inherit; }
    .doc-candidate-name { font-size: 24px; font-weight: 600; text-align: center; margin-bottom: 4px; }
    .doc-contact-line { font-size: 13px; text-align: center; color: #555; margin: 2px 0; }
    .doc-section-header { font-size: 13px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #000; padding-bottom: 3px; margin-top: 18px; margin-bottom: 6px; }
    .doc-paragraph { margin-bottom: 8px; color: #2a2a2a; }
    .doc-bullet-list { margin: 4px 0 10px 18px; }
    .doc-bullet-item { margin-bottom: 4px; color: #2a2a2a; }
    .doc-company-line { font-weight: 500; margin: 10px 0 0; }
    .doc-jobtitle-line { font-style: italic; margin: 0 0 4px; }
    .doc-scope-line { font-style: italic; margin: 0 0 4px; }
    .doc-greeting, .doc-closing { margin-bottom: 14px; }
    .doc-spacer { height: 10px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>${html}</body>
</html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 400);
}

/* ── Component ── */
const DocumentPreview = ({ result, isLoading, onDownload, onNewGeneration }) => {
  const [copied, setCopied] = useState(false);
  const label = documentConfig[result?.docType]?.label || result?.docType || 'Document';

  const documentContent = useMemo(
    () => renderDocumentContent(result?.resumeText, result?.docType),
    [result?.resumeText, result?.docType]
  );

  const handleCopy = () => {
    const text = cleanNumbersForDownload(result?.resumeText || '', result?.docType);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="doc-preview-panel">
      {/* Toolbar */}
      <div className="doc-preview-toolbar">
        <div className="doc-preview-toolbar-left">
          {onNewGeneration && (
            <button className="new-gen-btn" onClick={onNewGeneration}>
              ← New generation
            </button>
          )}
          <span className="doc-preview-title">{label}</span>
        </div>
        <div className="doc-preview-actions">
          <button
            className={`preview-action-btn ${copied ? 'preview-action-btn--copied' : ''}`}
            onClick={handleCopy}
            disabled={isLoading || result?.failed}
            title={result?.failed ? "Not available — this document couldn't be generated" : undefined}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            className="preview-action-btn"
            onClick={() => printDocument(result, label)}
            disabled={isLoading || result?.failed}
            title={result?.failed ? "Not available — this document couldn't be generated" : "Save as PDF via browser print"}
          >
            Save PDF
          </button>
          <button
            className="preview-action-btn preview-action-btn--primary"
            onClick={() => onDownload('docx', result)}
            disabled={isLoading || result?.failed}
            title={result?.failed ? "Not available — this document couldn't be generated" : undefined}
          >
            Download DOCX
          </button>
        </div>
      </div>

      {/* Page area */}
      <div className="doc-preview-scroll">
        {isLoading ? (
          <div className="doc-preview-loading">
            <div className="doc-preview-loading-spinner" />
            <span className="doc-preview-loading-text">Generating {label}…</span>
          </div>
        ) : result?.failed ? (
          <div className="doc-preview-failed">
            <div className="doc-preview-failed-icon">!</div>
            <p className="doc-preview-failed-text">{label} couldn't be generated.</p>
            <p className="doc-preview-failed-sub">
              Contact <a href="mailto:support@zynacular.com">support@zynacular.com</a> for help.
            </p>
          </div>
        ) : (
          <div className="doc-page">
            {documentContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentPreview;
